// src/AppLayout.jsx
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, Outlet, useLocation } from "react-router-dom";
import { jwtDecode } from "jwt-decode";

// Import the button component
import DraggableChatButton from './components/DraggableChatButton';

// API
import { 
  getUserJournal, 
  deletePost, 
  likePost, 
  unlikePost, 
  addComment, 
  deleteComment, 
  reportPost, 
  savePost, 
  unsavePost,
  getNotifications,
  getMarketAccess,
} from "./api";

// Extracted Components
import { NotificationStack } from "./components/NotificationStack";
import { MT5CredentialsModal } from "./components/MT5CredentialsModal";
import BottomNav from "./components/BottomNav";
import DesktopSidebar from "./components/DesktopSidebar";

// Custom Hooks
import { useNotifications } from "./hooks/useNotifications";
import { useMT5 } from "./hooks/useMT5";
import { useNews } from "./hooks/useNews";
import { useCommunityAPI } from "./hooks/useCommunityAPI";
import { useAuth } from "./context/AuthContext";

export default function AppLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth(); 

  const { alerts, showAlert, removeAlert } = useNotifications();
  const mt5HookData = useMT5(showAlert);
  const newsHookData = useNews();
  const { users, followUser, unfollowUser, isFollowing, hasUserLikedPost } = useCommunityAPI(); 

  const [journal, setJournal] = useState([]);
  const [userPosts, setUserPosts] = useState([]);
  const [notifications, setNotifications] = useState([]); // ✅ ADDED
  const [unreadCount, setUnreadCount] = useState(0); // ✅ ADDED
  const [marketAccess, setMarketAccess] = useState(false);
  const [marketAccessLoading, setMarketAccessLoading] = useState(true);
  const [marketAccessUserId, setMarketAccessUserId] = useState(null);

  useEffect(() => {
    let active = true;
    setMarketAccess(false);
    setMarketAccessUserId(null);
    setMarketAccessLoading(true);
    getMarketAccess()
      .then(({ data }) => {
        if (active) {
          setMarketAccess(data?.enabled === true);
          setMarketAccessUserId(currentUser?.id ?? null);
        }
      })
      .catch(() => { if (active) { setMarketAccess(false); setMarketAccessUserId(currentUser?.id ?? null); } })
      .finally(() => { if (active) setMarketAccessLoading(false); });
    return () => { active = false; };
  }, [currentUser?.id]);

  const currentUserMarketAccess = Boolean(currentUser?.id && marketAccessUserId === currentUser.id && marketAccess);
  const currentUserMarketAccessLoading = Boolean(currentUser?.id && marketAccessUserId !== currentUser.id) || marketAccessLoading;

  const handleLogout = () => {
    navigate("/auth");
  };

  // ===============================================================
  // ✅ NOTIFICATION HANDLERS
  // ===============================================================

  const fetchInAppNotifications = useCallback(async () => {
    try {
      const res = await getNotifications(); // Assumes this hits /notifications
      setNotifications(res.data || []);
      const unread = (res.data || []).filter(n => !n.is_read).length;
      setUnreadCount(unread);
    } catch (err) {
      console.error("Failed to fetch notifications", err);
    }
  }, []);

  useEffect(() => {
    fetchInAppNotifications();
    // Optional: Poll every 60 seconds for new notifications
    const interval = setInterval(fetchInAppNotifications, 60000);
    return () => clearInterval(interval);
  }, [fetchInAppNotifications]);

  // ===============================================================
  // COMMUNITY & POST HANDLERS
  // ===============================================================

  const handleDeletePost = async (postId) => {
    try {
      await deletePost(postId);
      setUserPosts(prev => prev.filter(p => p.id !== postId));
      showAlert("Post deleted", "success");
    } catch (err) {
      console.error("Delete failed", err);
      showAlert("Failed to delete post", "error");
    }
  };

  const handleLikePost = async (postId, isLiked = false) => {
    try {
      if (isLiked) {
        await unlikePost(postId);
      } else {
        await likePost(postId);
      }
      // Refresh notifications because a like might have triggered one (if we are the author)
      fetchInAppNotifications(); 
    } catch (err) {
      console.error("Like failed", err);
    }
  };

  const handleAddComment = async (postId, content, parentId = null) => {
    try {
      await addComment(postId, content, parentId);
      fetchInAppNotifications(); // Refresh for potential notifications
    } catch (err) {
      console.error("Comment failed", err);
    }
  };

  const handleReportPost = async (postId, reason) => {
    try {
      await reportPost(postId, reason);
      showAlert("Post reported", "success");
    } catch (err) {
      console.error("Report failed", err);
      showAlert("Failed to report post", "error");
    }
  };

  const handleSavePost = async (postId, isSaved = false) => {
    try {
      if (isSaved) {
        await unsavePost(postId);
      } else {
        await savePost(postId);
      }
    } catch (err) {
      console.error("Save failed", err);
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await deleteComment(commentId);
      // You might want to refresh userPosts here if needed
    } catch (err) {
      console.error("Delete comment failed", err);
    }
  };

  // ===============================================================
  // DATA FETCHING (Journal)
  // ===============================================================

  useEffect(() => {
    const fetchJournal = async () => {
      try {
        const res = await getUserJournal().catch((e) => ({ data: [] }));
        if (res?.data) setJournal(res.data);
      } catch (err) {
        console.error("Failed to fetch user journal:", err);
      }
    };
    fetchJournal();
  }, []);

  // ===============================================================
  // CONTEXT HELPERS
  // ===============================================================

  const commonProps = {
    fmt: (n, d = 2) => (n == null ? "—" : Number(n).toLocaleString(undefined, { maximumFractionDigits: d })),
    safe: (v) => (v == null ? 0 : Number(v)),
  };

  const getCurrentUserId = () => {
    if (currentUser && currentUser.id) return currentUser.id;
    try {
      const token = localStorage.getItem("token");
      if (token) {
        const decoded = jwtDecode(token);
        return decoded.user_id || decoded.id;
      }
    } catch (err) {
      console.error("Failed to decode token:", err);
    }
    return null;
  };

  const currentUserId = getCurrentUserId();

  // Check if we are on the settings page
  const isSettingsPage = location.pathname === '/dashboard/settings';

  return (
    <div className="relative h-screen flex flex-col bg-slate-50 dark:bg-gray-900 overflow-hidden">
      <NotificationStack alerts={alerts} onRemove={removeAlert} />

      <DesktopSidebar
        marketAccess={currentUserMarketAccess}
        isAdmin={Boolean(currentUser?.is_admin)}
        currentPath={location.pathname}
        unreadNotifications={unreadCount}
        onLogout={handleLogout}
        user={currentUser}
      />

      <main className="flex-grow overflow-y-auto overflow-x-hidden main-content md:pl-60">
        <Outlet
          context={{
            ...commonProps,
            ...mt5HookData,
            ...newsHookData,
            journal,
            currentUserId,
            handleLogout,
            users,
            currentUser,
            marketAccess: currentUserMarketAccess,
            marketAccessLoading: currentUserMarketAccessLoading,
            // Post Handlers
            deletePost: handleDeletePost,
            likePost: handleLikePost,
            addComment: handleAddComment,
            deleteComment: handleDeleteComment,
            reportPost: handleReportPost,
            savePost: handleSavePost,
            // Follow Handlers
            followUser,
            unfollowUser,
            isFollowing,
            hasUserLikedPost,
            // Data
            userPosts,
            setUserPosts,
            // ✅ NEW: Notification Context
            notifications,
            unreadCount,
            refreshNotifications: fetchInAppNotifications
          }}
        />
      </main>

      <BottomNav />

      <MT5CredentialsModal
        isOpen={mt5HookData.mt5ModalOpen}
        onClose={() => mt5HookData.setMt5ModalOpen(false)}
        form={mt5HookData.mt5Form}
        setForm={mt5HookData.setMt5Form}
        onSave={mt5HookData.handleSaveMt5}
        saving={mt5HookData.mt5Saving}
      />

      {/* Conditional Rendering: Only show DraggableChatButton on Settings */}
      {isSettingsPage && <DraggableChatButton />}
    </div>
  );
}
